import React, { useCallback, useState } from 'react';
import {
  Box, VStack, HStack, Text, Button, ButtonSpinner, ButtonText, Icon, Menu, MenuItem, MenuItemLabel, Pressable, Spinner,
} from '@gluestack-ui/themed';
import { MoreHorizontal } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { OralExamQuestionText } from '@/common/components/OralExamQuestionText';

import { OralExamConductPhase } from '@/common/api/models/oralExamConductPhase';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import { useMeetingConfirm } from '@/common/components/meeting/MeetingConfirm';
import {
  getOralExamApiErrorCode,
  getOralExamApiErrorMessage,
} from '@/common/hooks/oralExam/oralExamApiError';
import { useOralExamMutations } from '@/common/hooks/oralExam/useOralExamMutations';
import { resolveLecturerCtaGate } from '@/common/hooks/oralExam/oralExamCtaGateMatrix';
import type { OralExamRouteIds } from '@/common/hooks/oralExam/useOralExamRunStatePoll';
import { isOneToOneExamSurface } from '@/common/utils/oralExam/runConduct';

export type OralExamControlPanelProps = {
  ids: OralExamRouteIds;
  runState: OralExamRunStateResponse;
  stateQueryKey: readonly unknown[];
  onLeaveRoom?: () => void;
};

function actionErrorMessage(error: unknown, fallback: string): string {
  const code = getOralExamApiErrorCode(error);
  if (code === 'question_pool_empty') {
    return fallback;
  }
  return getOralExamApiErrorMessage(error, fallback);
}

/**
 * In-progress conducting controls (Next / Run again / Abort / End exam) for the
 * lecturer, designed to sit in the single bottom AV bar (MeetingControlBar's
 * `conductingControls` slot) — never a separate stacked row, and never with an
 * answer key (that is an internal reasoning trace, not shown to the examiner).
 */
export function OralExamConductingControls({
  ids,
  runState,
  stateQueryKey,
  isDropIn = false,
  onReturnToOverview,
}: Omit<OralExamControlPanelProps, 'onLeaveRoom'> & {
  isDropIn?: boolean;
  onReturnToOverview?: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const { confirm } = useMeetingConfirm();
  const [actionError, setActionError] = useState<string | null>(null);
  const [isReturning, setIsReturning] = useState(false);
  const [returnError, setReturnError] = useState<string | null>(null);
  const {
    advanceQuestion,
    endExam,
    abortExam,
    resetExam,
    resetTimer,
    repeatPreviousQuestion,
    isAdvancing,
    isEnding,
    isAborting,
    isResetting,
    isResettingTimer,
    isRepeatingPrevious,
  } = useOralExamMutations(ids, { etag: runState.etag ?? null, stateQueryKey });

  const pending = isAdvancing || isEnding || isAborting || isResetting
    || isResettingTimer || isRepeatingPrevious;
  const isOneToOne = isOneToOneExamSurface(runState);
  const ctaGate = resolveLecturerCtaGate({
    role: 'lecturer',
    runState: runState.state,
    conductPhase: runState.conductPhase ?? OralExamConductPhase.in_exam,
    isPaused: runState.isPaused,
    pauseReason: runState.pauseReason,
    studentDoneAt: runState.studentDoneAt,
    currentQuestion: runState.currentQuestion,
    currentQuestionIndex: runState.currentQuestionIndex,
    operationalFault: runState.operationalFault,
    driver: runState.driver,
    isMutationPending: pending,
    committed: runState.committed,
    stageGateOk: runState.stageGate?.ok,
    mode: runState.mode,
    capacity: runState.capacity,
  });
  const currentQuestionIndex = runState.currentQuestionIndex ?? 0;
  const totalQuestions = runState.selectedQuestionIds?.length ?? 0;
  const isLastQuestion = totalQuestions > 0 && currentQuestionIndex >= totalQuestions - 1;

  const runAction = useCallback(async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await fn();
    } catch (error) {
      // useOralExamMutations already toasts; keep an in-bar message too so a
      // fullscreen meeting never hides the failure behind an invisible toast/dialog.
      const message = actionErrorMessage(error, t('oralExam.actionFailed'));
      if (typeof message === 'string' && /paused/i.test(message)) {
        setActionError(t('oralExam.examStillPaused'));
      } else {
        setActionError(message);
      }
    }
  }, [t]);

  const handleNext = useCallback(() => {
    runAction(() => advanceQuestion()).catch(() => undefined);
  }, [advanceQuestion, runAction]);

  const handleDropInNext = useCallback(async () => {
    const confirmed = await confirm({
      title: t('oralExam.dropInNextConfirmTitle', {
        defaultValue: 'Move on to the next question?',
      }),
      message: t('oralExam.dropInNextConfirmMessage', {
        defaultValue: 'Once you leave the student, the AI examiner asks it.',
      }),
      confirmLabel: t('oralExam.lecturer.goToNextQuestion', {
        defaultValue: 'Go to next question',
      }),
      destructive: false,
    });
    if (!confirmed) return;
    await runAction(() => advanceQuestion());
  }, [advanceQuestion, confirm, runAction, t]);

  const handleEnd = useCallback(async () => {
    const confirmed = await confirm({
      title: t('oralExam.endExamConfirmTitle', { defaultValue: 'End exam?' }),
      message: t('oralExam.endExamConfirmMessage', {
        defaultValue: 'This ends the conference and submits the student\'s work for grading.',
      }),
      confirmLabel: t('oralExam.endExam'),
      destructive: true,
    });
    if (!confirmed) return;
    await runAction(() => endExam());
  }, [confirm, endExam, runAction, t]);

  const handleAbort = useCallback(async () => {
    const confirmed = await confirm({
      title: t('oralExam.abortConfirmTitle'),
      message: t(
        runState.committed
          ? 'oralExam.abortConfirmMessageCommitted'
          : 'oralExam.abortConfirmMessage',
      ),
      confirmLabel: t('oralExam.abortExam'),
      destructive: true,
    });
    if (confirmed) await runAction(() => abortExam());
  }, [abortExam, confirm, runAction, runState.committed, t]);

  const handleResetTimer = useCallback(async () => {
    const confirmed = await confirm({
      title: t('oralExam.resetTimerConfirmTitle', { defaultValue: 'Reset the timer?' }),
      message: t('oralExam.resetTimerConfirmMessage', {
        defaultValue: 'The student gets the full answering time again for this question.',
      }),
      confirmLabel: t('oralExam.resetTimer', { defaultValue: 'Reset timer' }),
      destructive: false,
    });
    if (confirmed) await runAction(() => resetTimer());
  }, [confirm, resetTimer, runAction, t]);

  const handleRepeatPrevious = useCallback(async () => {
    const confirmed = await confirm({
      title: t('oralExam.repeatPreviousQuestionConfirmTitle', {
        defaultValue: 'Discard the previous answer?',
      }),
      message: t('oralExam.repeatPreviousQuestionConfirmMessage', {
        defaultValue:
          'This student goes back to the last question. Their previous answer to that question will be discarded, and the answering timer starts again.',
      }),
      confirmLabel: t('oralExam.repeatPreviousQuestion', {
        defaultValue: 'Repeat previous question',
      }),
      destructive: true,
    });
    if (confirmed) await runAction(() => repeatPreviousQuestion());
  }, [confirm, repeatPreviousQuestion, runAction, t]);

  const handleReset = useCallback(async () => {
    const confirmed = await confirm({
      title: t('oralExam.runAgainConfirmTitle'),
      message: t('oralExam.runAgainConfirmMessage'),
      confirmLabel: t('oralExam.runAgain'),
      destructive: true,
    });
    if (confirmed) await runAction(() => resetExam());
  }, [confirm, resetExam, runAction, t]);

  const handleReturnToOverview = useCallback(async () => {
    if (!onReturnToOverview || isReturning) return;
    setIsReturning(true);
    setReturnError(null);
    try {
      await onReturnToOverview();
    } catch (error) {
      setReturnError(actionErrorMessage(error, t('oralExam.actionFailed')));
    } finally {
      setIsReturning(false);
    }
  }, [isReturning, onReturnToOverview, t]);

  if (isDropIn) {
    return (
      <VStack space="xs" testID="oral-exam-conducting-controls">
        {actionError ? (
          <Text size="xs" color="$statusError" testID="oral-exam-conducting-action-error">
            {actionError}
          </Text>
        ) : null}
        {returnError ? (
          <Text size="xs" color="$statusError" testID="oral-exam-return-error">
            {returnError}
          </Text>
        ) : null}
        <HStack space="sm" alignItems="center" flexWrap="wrap">
          <Button
            size="sm"
            action="primary"
            onPress={() => { handleReturnToOverview().catch(() => undefined); }}
            isDisabled={isReturning}
            testID="lecturer-back-to-cohort"
          >
            {isReturning ? <ButtonSpinner /> : null}
            <ButtonText>
              {t('oralExam.lecturer.backToOverview', { defaultValue: 'Return to overview' })}
            </ButtonText>
          </Button>
          <Box testID="oral-exam-engage-actions">
            <Menu
              placement="top"
              trigger={({ ...triggerProps }) => (
                <Pressable
                  {...triggerProps}
                  accessibilityRole="button"
                  accessibilityLabel={t('oralExam.lecturer.engageActions', {
                    defaultValue: 'Exam actions',
                  })}
                >
                  <Box
                    px="$3"
                    py="$2"
                    borderRadius="$md"
                    borderWidth={1}
                    borderColor="$borderLight"
                    bg="$bgApp"
                  >
                    <Icon as={MoreHorizontal} size="sm" color="$textHeading" />
                  </Box>
                </Pressable>
              )}
            >
              <MenuItem
                key="next"
                textValue={t('oralExam.nextQuestion', { defaultValue: 'Go to next question' })}
                onPress={() => { handleDropInNext().catch(() => undefined); }}
                disabled={!ctaGate.advanceQuestion || isLastQuestion}
                testID="oral-exam-next"
              >
                <MenuItemLabel size="sm">
                  {t('oralExam.lecturer.goToNextQuestion', { defaultValue: 'Go to next question' })}
                </MenuItemLabel>
              </MenuItem>
              <MenuItem
                key="repeat"
                textValue={t('oralExam.repeatPreviousQuestion', { defaultValue: 'Repeat previous question' })}
                onPress={() => { handleRepeatPrevious().catch(() => undefined); }}
                disabled={!ctaGate.repeatPrevious}
                testID="oral-exam-repeat-previous"
              >
                <MenuItemLabel size="sm">
                  {t('oralExam.lecturer.repeatPreviousQuestion', {
                    defaultValue: 'Repeat previous question',
                  })}
                </MenuItemLabel>
              </MenuItem>
              <MenuItem
                key="reset-timer"
                textValue={t('oralExam.resetTimer', { defaultValue: 'Reset timer for current question' })}
                onPress={() => { handleResetTimer().catch(() => undefined); }}
                disabled={!ctaGate.resetTimer}
                testID="oral-exam-reset-timer"
              >
                <MenuItemLabel size="sm">
                  {t('oralExam.lecturer.resetTimerForQuestion', {
                    defaultValue: 'Reset timer for current question',
                  })}
                </MenuItemLabel>
              </MenuItem>
              <MenuItem
                key="abort"
                textValue={t('oralExam.abortExam', { defaultValue: "Abort this student's exam" })}
                onPress={() => { handleAbort().catch(() => undefined); }}
                disabled={!ctaGate.abortExam}
                testID="oral-exam-abort"
              >
                <MenuItemLabel size="sm" color="$statusError">
                  {t('oralExam.lecturer.abortThisStudentsExam', {
                    defaultValue: "Abort this student's exam",
                  })}
                </MenuItemLabel>
              </MenuItem>
            </Menu>
          </Box>
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack space="xs" testID="oral-exam-conducting-controls">
      {actionError ? (
        <Text size="xs" color="$statusError" testID="oral-exam-conducting-action-error">
          {actionError}
        </Text>
      ) : null}
      <HStack space="sm" alignItems="center" flexWrap="wrap">
        {isLastQuestion ? (
          <Button
            size="sm"
            action="primary"
            onPress={handleEnd}
            isDisabled={!ctaGate.endExam}
            testID="oral-exam-end"
          >
            {isEnding
              ? <Spinner size="small" />
              : <ButtonText>{t('oralExam.endExam')}</ButtonText>}
          </Button>
        ) : (
          <Button
            size="sm"
            action="primary"
            onPress={handleNext}
            isDisabled={!ctaGate.advanceQuestion}
            testID="oral-exam-next"
          >
            {isAdvancing
              ? <Spinner size="small" />
              : <ButtonText>{t('oralExam.nextQuestion')}</ButtonText>}
          </Button>
        )}
        {!isOneToOne ? (
          <Button
            size="sm"
            variant="outline"
            action="secondary"
            onPress={handleRepeatPrevious}
            isDisabled={!ctaGate.repeatPrevious}
            testID="oral-exam-repeat-previous"
          >
            {isRepeatingPrevious ? <Spinner size="small" /> : (
              <ButtonText>
                {t('oralExam.repeatPreviousQuestion', { defaultValue: 'Repeat previous question' })}
              </ButtonText>
            )}
          </Button>
        ) : null}
        {!isOneToOne ? (
          <Button
            size="sm"
            variant="outline"
            action="secondary"
            onPress={handleReset}
            isDisabled={!ctaGate.resetExam}
            testID="oral-exam-reset"
          >
            {isResetting ? <Spinner size="small" /> : <ButtonText>{t('oralExam.runAgain')}</ButtonText>}
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="outline"
          action="secondary"
          onPress={handleResetTimer}
          isDisabled={!ctaGate.resetTimer}
          testID="oral-exam-reset-timer"
        >
          {isResettingTimer ? <Spinner size="small" /> : (
            <ButtonText>{t('oralExam.resetTimer', { defaultValue: 'Reset timer' })}</ButtonText>
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          action="negative"
          onPress={handleAbort}
          isDisabled={!ctaGate.abortExam}
          testID="oral-exam-abort"
        >
          {isAborting ? <Spinner size="small" /> : <ButtonText>{t('oralExam.abortExam')}</ButtonText>}
        </Button>
      </HStack>
    </VStack>
  );
}

/**
 * Lecturer oral exam panel for the non-conducting phases: the pre-start question
 * list (start/redraw) and the terminal (run again / leave) panel. The in-progress
 * conducting controls live in the bottom AV bar via OralExamConductingControls.
 */
export function OralExamControlPanel({
  ids,
  runState,
  stateQueryKey,
  onLeaveRoom,
}: OralExamControlPanelProps) {
  const { t } = useTranslation();
  const { confirm } = useMeetingConfirm();
  const {
    startExam,
    redrawQuestions,
    resetExam,
    isStarting,
    isRedrawing,
    isResetting,
  } = useOralExamMutations(ids, { etag: runState.etag ?? null, stateQueryKey });

  const handleReset = useCallback(async () => {
    const confirmed = await confirm({
      title: t('oralExam.runAgainConfirmTitle'),
      message: t('oralExam.runAgainConfirmMessage'),
      confirmLabel: t('oralExam.runAgain'),
      destructive: true,
    });
    if (confirmed) resetExam();
  }, [resetExam, confirm, t]);

  const pending = isStarting || isRedrawing || isResetting;

  if (runState.state === 'pre_start') {
    const draftQuestions = runState.draftQuestions ?? [];
    return (
      <VStack space="sm" p="$3" testID="oral-exam-control-pre-start">
        <Text size="xs" color="$textSubtle" fontWeight="bold">
          {t('oralExam.preStartTitle')}
        </Text>
        <Box
          maxHeight={200}
          overflow="scroll"
          sx={{
            _web: {
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollbarGutter: 'auto',
            },
          }}
        >
          <VStack space="sm">
            {draftQuestions.map((item, idx) => (
              <Box key={item.questionId} p="$2" bg="$bgSubtle" borderRadius="$sm">
                <Text size="xs" color="$textSubtle" fontWeight="bold">
                  {t('oralExam.draftQuestionNumber', { index: idx + 1 })}
                </Text>
                <OralExamQuestionText
                  question={item.question ?? ''}
                  size="sm"
                  titleColor="$textHeading"
                  textColor="$textHeading"
                />
              </Box>
            ))}
          </VStack>
        </Box>
        <HStack space="sm">
          <Button
            size="sm"
            variant="outline"
            action="secondary"
            onPress={() => redrawQuestions()}
            isDisabled={pending}
            testID="oral-exam-redraw"
          >
            {isRedrawing ? <Spinner size="small" /> : <ButtonText>{t('oralExam.redraw')}</ButtonText>}
          </Button>
          <Button
            size="sm"
            action="primary"
            onPress={() => startExam()}
            isDisabled={pending || draftQuestions.length === 0}
            testID="oral-exam-start"
          >
            {isStarting ? <Spinner size="small" /> : <ButtonText>{t('oralExam.startExam')}</ButtonText>}
          </Button>
        </HStack>
      </VStack>
    );
  }

  if (runState.state === 'in_progress') {
    const isOneToOne = isOneToOneExamSurface(runState);
    const awaitingStart = isOneToOne && !runState.currentQuestion?.question?.trim();
    if (awaitingStart) {
      const ctaGate = resolveLecturerCtaGate({
        role: 'lecturer',
        runState: runState.state,
        conductPhase: runState.conductPhase ?? OralExamConductPhase.in_exam,
        isPaused: runState.isPaused,
        currentQuestion: runState.currentQuestion,
        isMutationPending: isStarting,
        committed: runState.committed,
        mode: runState.mode,
        capacity: runState.capacity,
      });
      return (
        <VStack space="sm" p="$3" testID="oral-exam-control-start-exam">
          <Text size="sm" color="$textBody">
            {t('oralExam.lecturer.conferenceReady', {
              defaultValue: 'You are connected. Start the exam when you are ready.',
            })}
          </Text>
          <Button
            size="sm"
            action="primary"
            onPress={() => startExam()}
            isDisabled={!ctaGate.startExam || isStarting}
            testID="oral-exam-start"
          >
            {isStarting ? <Spinner size="small" /> : <ButtonText>{t('oralExam.startExam')}</ButtonText>}
          </Button>
        </VStack>
      );
    }
    // In-progress conducting controls live in the single bottom AV bar
    // (see OralExamConductingControls / MeetingControlBar), not a separate row.
    // The answer key is an internal reasoning trace and is never shown to the
    // conducting lecturer.
    return null;
  }

  const isOneToOne = isOneToOneExamSurface(runState);
  if (
    isOneToOne
    && (runState.state === 'completed' || runState.state === 'aborted')
  ) {
    return null;
  }

  const terminalKey = runState.state === 'aborted' ? 'oralExam.terminalAborted' : 'oralExam.terminalCompleted';
  const showTerminalReset = runState.committed !== true && runState.stageGate?.ok !== false;
  return (
    <VStack space="sm" p="$3" testID="oral-exam-control-terminal">
      <Text size="sm" color="$textBody">{t(terminalKey)}</Text>
      <HStack space="sm">
        {showTerminalReset ? (
          <Button size="sm" action="primary" onPress={handleReset} isDisabled={pending} testID="oral-exam-reset">
            {isResetting ? <Spinner size="small" /> : <ButtonText>{t('oralExam.runAgain')}</ButtonText>}
          </Button>
        ) : null}
        {!!onLeaveRoom && (
          <Button size="sm" variant="outline" action="secondary" onPress={onLeaveRoom} testID="oral-exam-leave-room">
            <ButtonText>{t('oralExam.leaveRoom')}</ButtonText>
          </Button>
        )}
      </HStack>
    </VStack>
  );
}
